/**
 * /smartlinks/capacity — SmartLink capacity-aware routing page.
 *
 * Mirrors the contract declared by the API feature #113 (OPUS-113):
 *   - PUT    /api/v1/smartlink-capacity
 *   - GET    /api/v1/smartlink-capacity/:offer_id
 *   - POST   /api/v1/smartlink-capacity/increment
 *   - POST   /api/v1/smartlink-capacity/filter
 *
 * Declares a TanStack Router route and renders the headline + the
 * `SmartlinkCapacityPage` shell. The route auto-registers against the
 * scaffolded `rootRoute` via a module augmentation in the scaffold's
 * `router.tsx` — any future agent / the root wire-up cycle picks it up
 * by importing from this module.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { SmartlinkCapacityPage } from "@/features/smartlinks-capacity/SmartlinkCapacityPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/smartlinks/capacity`.
// `validateSearch` reserves room for an `?offerId=` deep-link so operators
// can open the page from an offer detail view with the ID prefilled.
// ---------------------------------------------------------------------------

interface SmartlinkCapacitySearch {
	offerId?: string;
}

export const smartlinksCapacityRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/smartlinks/capacity",
	validateSearch: (raw: Record<string, unknown>): SmartlinkCapacitySearch => {
		const offerId = raw.offerId;
		if (typeof offerId === "string" && offerId.trim().length > 0) {
			return { offerId: offerId.trim() };
		}
		return {};
	},
	component: SmartlinksCapacityRouteComponent,
});

export default smartlinksCapacityRoute;

// ---------------------------------------------------------------------------
// Page — static shell + the capacity operator surface. No client-side auth
// wiring yet; the global `apiClient` handles the `x-tenant-id` header.
// ---------------------------------------------------------------------------

export function SmartlinksCapacityRouteComponent() {
	const { offerId } = smartlinksCapacityRoute.useSearch();

	return (
		<main className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<SmartlinkCapacityPage initialOfferId={offerId} />
			<DocHint />
		</main>
	);
}

// ---------------------------------------------------------------------------
// Header fragments — purely presentational. No emojis, no competitor names.
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">SmartLinks</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Capacity</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				SmartLinks
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Capacity-aware routing
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Cap each offer by daily, monthly, or lifetime conversions. The
				SmartLink router skips any offer whose current-window counter
				has reached its cap, so budget never overshoots.
			</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					Daily caps use a YYYY-MM-DD window, monthly caps use YYYY-MM,
					and lifetime caps reuse the literal key 'lifetime'.
				</li>
				<li>
					The router calls /filter on every SmartLink pick to drop any
					offer that hit its cap for the active window.
				</li>
				<li>
					Conversions POST /increment — the response signals over-cap so
					downstream analytics stay consistent.
				</li>
			</ul>
		</aside>
	);
}
